import { ReactNode, useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "@/lib/trpc";
import { clearClarityOneCustomerQueryCache } from "@/lib/clarityOneQueryCache";
import ClarityLogo from "@/assets/clarity-logo-light.svg";
import { CLARITY_ONE_COLORS, CLARITY_ONE_FONT } from "@shared/clarityOneTheme";
import { listPrimaryClarityOneNavModules } from "@shared/clarityOneModules";
import { LogOut, Menu, X } from "lucide-react";

type ClarityOneLayoutProps = {
  children: ReactNode;
  /** When true, show nav and logout (authenticated shell). */
  showNav?: boolean;
};

const DRAWER_NAV_ID = "clarity-one-nav-drawer";

export function isNavActive(pathname: string, modulePath: string, moduleId: string): boolean {
  if (moduleId === "home") return pathname === "/home" || pathname === "/";
  if (moduleId === "proposals") {
    return pathname.startsWith("/proposals") || pathname.startsWith("/vo/");
  }
  if (moduleId === "statement") {
    return pathname === "/account/statement" || pathname.startsWith("/account/statement/");
  }
  if (moduleId === "account") {
    // Statement is its own nav item — don't highlight My Account for /account/statement
    if (pathname === "/account/statement" || pathname.startsWith("/account/statement/")) {
      return false;
    }
    return pathname === "/account" || pathname.startsWith("/account/");
  }
  return pathname === modulePath || pathname.startsWith(`${modulePath}/`);
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const nodes = container.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
  );
  return Array.from(nodes).filter(
    (el) => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true",
  );
}

export default function ClarityOneLayout({ children, showNav = true }: ClarityOneLayoutProps) {
  const [location] = useLocation();
  const pathname = location.split("?")[0] || location;
  const [navOpen, setNavOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement | null>(null);
  const drawerRef = useRef<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const titleId = useId();
  const queryClient = useQueryClient();

  const navModules = listPrimaryClarityOneNavModules();

  const logout = trpc.clarityOneAuth.logout.useMutation({
    onSuccess: () => {
      clearClarityOneCustomerQueryCache(queryClient);
      window.location.href = "/login";
    },
  });

  const closeNav = (options?: { restoreFocus?: boolean }) => {
    const restoreFocus = options?.restoreFocus !== false;
    setNavOpen(false);
    if (restoreFocus) {
      // Defer until after the drawer unmounts so focus is not trapped on a removed node.
      queueMicrotask(() => {
        hamburgerRef.current?.focus();
      });
    }
  };

  const onCloseNavClick = () => {
    closeNav({ restoreFocus: true });
  };

  useEffect(() => {
    // Route changes close the drawer; focus follows the new page.
    setNavOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!navOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeNav({ restoreFocus: true });
        return;
      }
      if (event.key !== "Tab" || !drawerRef.current) return;
      const focusable = getFocusableElements(drawerRef.current);
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (event.shiftKey) {
        if (active === first || !drawerRef.current.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [navOpen]);

  const navLinkClass = (active: boolean) =>
    [
      "block w-full rounded-md px-3 py-2.5 text-left text-[12px] tracking-[0.16em] uppercase no-underline transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B8956E] focus-visible:ring-offset-2",
      active
        ? "bg-[#F0EDE6] text-[#1A2744] font-medium border-l-2 border-[#B8956E]"
        : "text-[#1A2744]/80 hover:bg-[#F0EDE6] hover:text-[#1A2744] border-l-2 border-transparent",
    ].join(" ");

  return (
    <div
      className="clarity-one min-h-screen flex flex-col"
      style={{
        backgroundColor: CLARITY_ONE_COLORS.cream,
        color: CLARITY_ONE_COLORS.navy,
        fontFamily: CLARITY_ONE_FONT,
      }}
    >
      <header
        className="sticky top-0 z-20 border-b border-[#E8E4DC] bg-[#F9F7F2]/95 backdrop-blur-sm"
        data-testid="clarity-one-header"
      >
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-5 sm:px-8 md:h-auto md:items-start md:gap-6 md:px-8 md:py-5 lg:px-10">
          {showNav ? (
            <button
              ref={hamburgerRef}
              type="button"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E8E4DC] text-[#1A2744] transition-colors hover:bg-[#F0EDE6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B8956E] focus-visible:ring-offset-2"
              aria-label="Open navigation menu"
              aria-expanded={navOpen}
              aria-controls={DRAWER_NAV_ID}
              data-testid="clarity-one-nav-toggle"
              onClick={() => setNavOpen(true)}
            >
              <Menu className="h-5 w-5" strokeWidth={1.75} aria-hidden />
            </button>
          ) : (
            <div className="flex-1" />
          )}

          <Link
            href={showNav ? "/home" : "/login"}
            className="shrink-0 no-underline"
            aria-label="Clarity"
            data-testid="clarity-one-logo-link"
          >
            <img
              src={ClarityLogo}
              alt="Clarity"
              className="h-9 w-auto object-contain md:h-12"
              style={{ maxWidth: "16.5rem" }}
              data-testid="clarity-one-logo"
            />
          </Link>
        </div>
      </header>

      {showNav && navOpen ? (
        <div className="fixed inset-0 z-50" data-testid="clarity-one-nav-layer">
          <button
            type="button"
            className="absolute inset-0 bg-[#1A2744]/35"
            aria-label="Close navigation menu"
            data-testid="clarity-one-nav-backdrop"
            onClick={onCloseNavClick}
          />
          <aside
            ref={drawerRef}
            id={DRAWER_NAV_ID}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="absolute inset-y-0 left-0 flex w-[min(20rem,88vw)] max-w-full flex-col border-r border-[#E8E4DC] bg-[#F9F7F2] shadow-lg"
            data-testid="clarity-one-nav-drawer"
          >
            <div className="flex items-center justify-between border-b border-[#E8E4DC] px-4 py-3">
              <p
                id={titleId}
                className="text-[11px] tracking-[0.18em] uppercase text-[#B8956E]"
              >
                Menu
              </p>
              <button
                ref={closeButtonRef}
                type="button"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#E8E4DC] text-[#1A2744] transition-colors hover:bg-[#F0EDE6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B8956E] focus-visible:ring-offset-2"
                aria-label="Close navigation menu"
                data-testid="clarity-one-nav-close"
                onClick={onCloseNavClick}
              >
                <X className="h-4 w-4" strokeWidth={1.75} aria-hidden />
              </button>
            </div>

            <nav
              className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-4"
              aria-label="Primary"
              data-testid="clarity-one-nav-menu"
            >
              {navModules.map((mod) => {
                const active = isNavActive(pathname, mod.path, mod.id);
                return (
                  <Link
                    key={mod.id}
                    href={mod.path}
                    className={navLinkClass(active)}
                    aria-current={active ? "page" : undefined}
                    onClick={onCloseNavClick}
                    data-testid={`clarity-one-nav-${mod.id}`}
                    data-active={active ? "true" : "false"}
                  >
                    {mod.label}
                  </Link>
                );
              })}
            </nav>

            <div className="border-t border-[#E8E4DC] px-3 py-4">
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-left text-[12px] uppercase tracking-[0.16em] text-[#8B7355] transition-colors hover:bg-[#F0EDE6] hover:text-[#1A2744] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B8956E] focus-visible:ring-offset-2"
                onClick={() => {
                  closeNav({ restoreFocus: true });
                  logout.mutate();
                }}
                disabled={logout.isPending}
                aria-label="Log out"
                data-testid="clarity-one-nav-logout"
              >
                <LogOut className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden />
                Log Out
              </button>
            </div>
          </aside>
        </div>
      ) : null}

      <main className="mx-auto w-full max-w-6xl flex-1 px-5 py-8 sm:px-8 sm:py-10 lg:px-10 lg:py-12">
        {children}
      </main>

      <footer className="border-t border-[#E8E4DC] px-5 py-8 text-center">
        <p className="text-[11px] tracking-wide text-[#8B7355]">
          © {new Date().getFullYear()} Clarity Group Pte. Ltd. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
